import { startOfDay } from "date-fns";
import { mapValues, orderBy } from "lodash-es";
import { combineReducers } from "redux";
import { createReducer } from "typesafe-actions";

import localSettings from "@app/config/LocalSettings";
import { parseApiDate, parseApiDateWithTz } from "@app/util/parsers";

import {
  colorOrShapeKeyChanged,
  disposeUserLocations,
  fetchCheckInPins,
  fetchEvents,
  fetchWidgetsData,
  initUserLocations,
  invalidateWidgetsData,
  selectLocation,
  setDisabledUserIds,
  setHighlightedLocationId,
  setHoveredTrailPoint,
  setHoveredUserLocationDayGroup,
  setManualHeatmapMode,
  setPlayback,
  setSelectedDate,
  setSelectedDatePeriod,
  setSelectedUserIds,
  setShowOtherPins,
  setUserLocationsSettings,
  UserLocationActions,
} from "./actions";
import UserLocationDatePeriod from "./enums/UserLocationDatePeriod";
import { IDLE_PLAYBACK_STATE } from "./playback/consts";
import playback from "./playback/reducers";
import UserLocationsState from "./types/UserLocationsState";
import UserLocationsViewState from "./types/UserLocationsViewState";
import resolveDateForDatePeriodChange from "./utils/resolveDateForDatePeriodChange";

const viewInitialState: UserLocationsViewState = {
  accountPins: {},
  contactPins: {},
  disabledUserIds: [],
  events: [],
  eventsDateRange: undefined,
  loading: false,
  pinsLoading: false,
  selectedDate: undefined,
  selectedDatePeriod: UserLocationDatePeriod.Day,
  selectedUserIds: [],
  showOtherPins: true,
  widgetsData: undefined,
  widgetsDataDateRange: undefined,
  widgetsDataLoading: false,
};

export const userLocationInitialState: UserLocationsState = {
  legendExpanded: false,
  playback: IDLE_PLAYBACK_STATE,
  view: viewInitialState,
  visible: false,
};

// Map/timeline view slice — every field except the sibling playback and BaseLayerData-flag slices.
const view = createReducer<UserLocationsViewState, UserLocationActions>(viewInitialState)
  .handleAction(setSelectedUserIds, (state, { payload }) => {
    const isClosingPanel = payload.length === 0;
    if (isClosingPanel) {
      return {
        ...state,
        events: [],
        eventsDateRange: undefined,
        highlightedLocationId: undefined,
        hoveredTrailPoint: undefined,
        hoveredUserLocationDayGroup: undefined,
        manualHeatmapMode: undefined,
        selectedDate: undefined,
        selectedLocationId: undefined,
        selectedUserIds: payload,
        widgetsData: undefined,
        widgetsDataDateRange: undefined,
        widgetsDataLoading: false,
      };
    }
    const selectedUserIdSet = new Set(payload);
    return {
      ...state,
      events: [],
      eventsDateRange: undefined,
      highlightedLocationId: undefined,
      hoveredTrailPoint: undefined,
      hoveredUserLocationDayGroup: undefined,
      selectedLocationId: undefined,
      selectedUserIds: payload,
      showOtherPins:
        state.selectedUserIds.length === 0
          ? localSettings.getUserLocationsShowOtherPins()
          : state.showOtherPins,
      widgetsData: state.widgetsData?.filter((item) => selectedUserIdSet.has(item.userId)),
    };
  })
  // Drop the selection too: MapPathMarkers pans to selectedLocationId when it remounts, so leaving
  // it set would drag the camera back on every switch into Activity.
  .handleAction(setManualHeatmapMode, (state, { payload }) => ({
    ...state,
    manualHeatmapMode: payload,
    selectedLocationId: undefined,
  }))
  .handleAction(setShowOtherPins, (state, { payload }) => ({
    ...state,
    showOtherPins: payload,
  }))
  // The playback value itself lives in the playback slice; here a commit only drops the trail hover
  // so a play/scrub can't leave a stale highlight. A play START additionally drops the timeline
  // selection: the playhead then owns what the map reveals, so a parked selection ring would
  // contradict it. A scrub commit carries isPlaying false and keeps the selection, so dragging the
  // rail never silently loses it.
  .handleAction(setPlayback, (state, { payload }) => ({
    ...state,
    hoveredTrailPoint: undefined,
    hoveredUserLocationDayGroup: undefined,
    selectedLocationId: payload.isPlaying ? undefined : state.selectedLocationId,
  }))
  .handleAction(setSelectedDate, (state, { payload }) => ({
    ...state,
    selectedDate: payload,
  }))
  // Keeps today in view across granularity switches; see resolveDateForDatePeriodChange for the
  // fallbacks when the old period does not contain today. Today is the org's calendar day, to match
  // the timezone the backend applies to the range selectedDate turns into.
  .handleAction(
    setSelectedDatePeriod,
    (state, { payload: { orgTimeZone, period, selectedDate } }) => ({
      ...state,
      selectedDate:
        selectedDate ??
        resolveDateForDatePeriodChange(
          state.selectedDatePeriod,
          period,
          state.selectedDate,
          startOfDay(parseApiDateWithTz(new Date(), orgTimeZone))
        ),
      selectedDatePeriod: period,
    })
  )
  .handleAction(fetchEvents.request, (state, { payload: { dateRange } }) => ({
    ...state,
    accountPins: {},
    contactPins: {},
    events: [],
    eventsDateRange: dateRange,
    highlightedLocationId: undefined,
    hoveredTrailPoint: undefined,
    hoveredUserLocationDayGroup: undefined,
    loading: true,
    pinsLoading: true,
    selectedLocationId: undefined,
  }))
  .handleAction(fetchEvents.success, (state, { payload: { events } }) => ({
    ...state,
    events: orderBy(events, (event) => parseApiDate(event.updatedAt).getTime(), "desc"),
    loading: false,
  }))
  .handleAction(fetchEvents.failure, (state) => ({
    ...state,
    events: [],
    eventsDateRange: undefined,
    loading: false,
    pinsLoading: false,
  }))
  .handleAction(fetchCheckInPins.success, (state, { payload: { accountPins, contactPins } }) => ({
    ...state,
    accountPins,
    contactPins,
    pinsLoading: false,
  }))
  .handleAction(fetchCheckInPins.request, (state) => ({
    ...state,
    pinsLoading: true,
  }))
  // Held in the slice rather than carried on the fetch action: takeLatest means an events-driven
  // refresh can cancel and replace the fetch, and a payload would go with it.
  .handleAction(colorOrShapeKeyChanged, (state, { payload }) => ({
    ...state,
    pendingColorKey: payload.colorKey ?? state.pendingColorKey,
    pendingShapeKey: payload.shapeKey ?? state.pendingShapeKey,
  }))
  // Reset the resolved colour rather than dropping the pins: convertTimelineEventToLocation only
  // builds a check-in when its pin is present, so emptying these would take the markers and the CSV
  // export with them. Ignored when no fetch is in flight, so a failure that lands after dispose
  // cannot write to the reset slice.
  .handleAction(fetchCheckInPins.failure, (state) =>
    state.pinsLoading
      ? {
          ...state,
          accountPins: mapValues(state.accountPins, (pin) => ({ ...pin, color: null })),
          contactPins: mapValues(state.contactPins, (pin) => ({ ...pin, color: null })),
          pinsLoading: false,
        }
      : state
  )
  .handleAction(initUserLocations.success, (state, { payload: { disabledUserIds } }) => ({
    ...state,
    disabledUserIds,
  }))
  .handleAction(setHighlightedLocationId, (state, { payload }) => ({
    ...state,
    highlightedLocationId: payload,
  }))
  .handleAction(setHoveredUserLocationDayGroup, (state, { payload }) => ({
    ...state,
    hoveredUserLocationDayGroup: payload,
  }))
  .handleAction(setHoveredTrailPoint, (state, { payload }) => ({
    ...state,
    hoveredTrailPoint: payload,
  }))
  .handleAction(selectLocation, (state, { payload }) => ({
    ...state,
    selectedLocationId: payload,
  }))
  .handleAction(setDisabledUserIds, (state, { payload }) => {
    const selectedUserIds = state.selectedUserIds.filter((id) => !payload.includes(id));
    return {
      ...state,
      disabledUserIds: payload,
      // Hiding the last selected rep closes the panel, so the view choice ends with that session
      // just as it does on the Exit path.
      manualHeatmapMode: selectedUserIds.length === 0 ? undefined : state.manualHeatmapMode,
      // Hiding the selected rep takes its events off the map, so the ring resolves to nothing. Drop the
      // selection with it, or the timeline card stays highlighted for a point no longer drawn.
      selectedLocationId: undefined,
      selectedUserIds,
    };
  })
  .handleAction(fetchWidgetsData.request, (state, { payload: { dateRange } }) => ({
    ...state,
    widgetsDataDateRange: dateRange,
    widgetsDataLoading: true,
  }))
  .handleAction(fetchWidgetsData.success, (state, { payload: { data } }) => ({
    ...state,
    widgetsData: [...(state.widgetsData ?? []), ...data],
    widgetsDataLoading: false,
  }))
  .handleAction(fetchWidgetsData.failure, (state) => ({
    ...state,
    widgetsDataLoading: false,
  }))
  .handleAction(invalidateWidgetsData, (state) => ({
    ...state,
    widgetsData: undefined,
    widgetsDataDateRange: undefined,
    widgetsDataLoading: false,
  }))
  .handleAction(disposeUserLocations, () => viewInitialState);

// The BaseLayerData flags are their own slices: the legend reads layer.visible / layer.legendExpanded
// uniformly across all map layers, so they must stay at the layer root.
const visible = createReducer<boolean, UserLocationActions>(false)
  .handleAction(setUserLocationsSettings, (state, { payload }) => payload?.visible ?? state)
  .handleAction(disposeUserLocations, () => false);

const legendExpanded = createReducer<boolean, UserLocationActions>(false)
  .handleAction(setUserLocationsSettings, (state, { payload }) => payload?.legendExpanded ?? state)
  .handleAction(disposeUserLocations, () => false);

// combineReducers carries no .handlers map, but the layers reducer delegates actions by it — pass the
// merged slice maps through.
const userLocationsLayer = Object.assign(
  combineReducers<UserLocationsState>({ legendExpanded, playback, view, visible }),
  {
    handlers: {
      ...legendExpanded.handlers,
      ...playback.handlers,
      ...view.handlers,
      ...visible.handlers,
    },
  }
);

export default userLocationsLayer;
